import React, { memo, useEffect } from "react";
import * as Yup from "yup";
import { formats, validators } from "investira.sdk";
import { useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import {
  Stack,
  Button,
  Icon,
  TextField,
  LoadingButton,
  Typography,
  IconButton,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Card,
  Tooltip,
  Box,
  TableContainer,
  Table,
  TableBody,
  TableCell,
  TableRow,
} from "investira.react.components";
import { useCarteirasContext } from "../../contexts/CarteirasContext";
import { useRecursoGarantidorContext } from "../../contexts/RecursoGarantidorContext";
import { VALIDATIONS } from "../../const";
import { CARTEIRA_TIPO } from "../../enums";
import CalendarPosicoesDialog from "./CalendarPosicoesDialog";

const Carteira = memo((props) => {
  const navigate = useNavigate();
  const { state, actions } = useCarteirasContext();
  const recursoContext = useRecursoGarantidorContext();
  const xRecurso = recursoContext.state.recursoSelected;

  const xTotalRecursoGarantidor = () => {
    if (validators.isEmpty(xRecurso)) {
      return 0;
    }
    return xRecurso.pl;
  };

  const xTipRecursosGarantidores = () => {
    if (validators.isEmpty(xRecurso)) {
      return null;
    }
    return (
      <Stack spacing={2} py={2} px={1}>
        <TableContainer>
          <Table size="small">
            <TableBody>
              <TableRow>
                <TableCell>
                  <Typography variant="caption" color="textPrimary">
                    Recurso Garantidor/Patrimônio líquido (PL)
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="caption" color="textPrimary">
                    <b>
                      {!validators.isNull(xRecurso.pl) &&
                        formats.formatNumber(xRecurso.pl, 2, true, true)}
                    </b>
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  <Typography variant="caption" color="textPrimary">
                    Cota
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="caption" color="textPrimary">
                    <b>
                      {!validators.isNull(xRecurso.cota) &&
                        formats.formatNumber(xRecurso.cota, 2, true, true)}
                    </b>
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  <Typography variant="caption" color="textPrimary">
                    Empréstimo
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="caption" color="textPrimary">
                    <b>
                      {!validators.isNull(xRecurso.emprestimo) &&
                        formats.formatNumber(
                          xRecurso.emprestimo,
                          2,
                          true,
                          true
                        )}
                    </b>
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  <Typography variant="caption" color="textPrimary">
                    Imóvel
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="caption" color="textPrimary">
                    <b>
                      {!validators.isNull(xRecurso.imovel) &&
                        formats.formatNumber(xRecurso.imovel, 2, true, true)}
                    </b>
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  <Typography variant="caption" color="textPrimary">
                    Caixa
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="caption" color="textPrimary">
                    <b>
                      {!validators.isNull(xRecurso.caixa) &&
                        formats.formatNumber(xRecurso.caixa, 2, true, true)}
                    </b>
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  <Typography variant="caption" color="textPrimary">
                    Provisões
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="caption" color="textPrimary">
                    <b>
                      {!validators.isNull(xRecurso.provisoes) &&
                        formats.formatNumber(xRecurso.provisoes, 2, true, true)}
                    </b>
                  </Typography>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Stack>
    );
  };

  const VALIDATION_SCHEMA = Yup.object().shape({
    carteira: Yup.string()
      .max(128, "Máximo de 128 caracteres")
      .required(VALIDATIONS.REQUIRED),
    carteira_seuid: Yup.string()
      .max(30, "Máximo de 30 caracteres")
      .required(VALIDATIONS.REQUIRED),
  });

  const formik = useFormik({
    initialValues: { carteira: "", carteira_seuid: "", carteira_tipo_id: "T" },
    validationSchema: VALIDATION_SCHEMA,
    validateOnMount: false,
    onSubmit: (pValues, pActions) => {
      pActions.setSubmitting(true);
      if (props.mode === "edit") {
        actions.modifyCarteira(
          { carteira_id: state.carteira_id, ...pValues },
          () => {
            actions.handleModifyCarteiraCancel();
          },
          null,
          () => {
            pActions.setSubmitting(false);
          }
        );
      } else {
        actions.createCarteira({ ...pValues }, null, null, () => {
          pActions.setSubmitting(false);
        });
      }
    },
  });

  useEffect(() => {
    props.id && actions.readCarteira(props.id);
  }, [props.id]);

  useEffect(() => {
    if (props.mode === "edit") {
      formik.setValues({
        carteira: state.carteira,
        carteira_seuid: state.carteira_seuid,
        carteira_tipo_id: state.carteira_tipo_id,
      });
    }
  }, [
    state.carteira,
    state.carteira_seuid,
    state.carteira_tipo_id,
    props.mode,
  ]);

  if (!validators.isNull(state.carteira_id) && props.mode === "read") {
    return (
      <Stack
        sx={{ gap: "8px 8px" }}
        flexWrap="wrap"
        direction="row"
        alignItems="center"
      >
        <Stack flexGrow={1}>
          <Card>
            <Stack px={2} py={2}>
              <Typography variant="caption">{state.carteira}</Typography>
              <Stack
                direction="row"
                spacing={2}
                alignItems="center"
                justifyContent="space-between"
              >
                <Stack>
                  <Typography variant="h6">{state.carteira_seuid}</Typography>
                </Stack>
                <Stack>
                  <IconButton
                    color="primary"
                    onClick={actions.handleModifyCarteira}
                  >
                    <Icon iconName="edit" size={18} />
                  </IconButton>
                </Stack>
              </Stack>
            </Stack>
          </Card>
        </Stack>
        <Stack flexGrow={1}>
          <Card>
            <Stack px={2} py={2}>
              <Typography variant="caption" color="textSecondary">
                Total Financeiro
              </Typography>
              <Stack direction="row" spacing={2} alignItems="center">
                <Stack>
                  <Typography variant="h6">
                    {formats.formatNumber(state.posicaoTotal, 2, true, true)}
                  </Typography>
                </Stack>
              </Stack>
            </Stack>
          </Card>
        </Stack>
        <Stack flexGrow={1}>
          <Card>
            <Stack px={2} py={2}>
              <Stack
                direction="row"
                spacing={2}
                alignItems="center"
                justifyContent="space-between"
              >
                <Stack>
                  <Typography variant="caption" color="textSecondary">
                    Recurso Garantidor
                  </Typography>
                </Stack>
                {!validators.isEmpty(xRecurso) && (
                  <Stack alignItems="center" justifyContent="center">
                    <Tooltip
                      title={xTipRecursosGarantidores()}
                      slotProps={{
                        tooltip: {
                          sx: {
                            maxWidth: 500, // ou 'none' para remover o limite
                            width: "100%", // opcional
                            fontSize: "1rem", // estilos adicionais
                          },
                        },
                      }}
                    >
                      <Box sx={{ width: "16px", height: "16px" }}>
                        <Icon
                          iconName="information"
                          size={16}
                          color="primary"
                        />
                      </Box>
                    </Tooltip>
                  </Stack>
                )}
              </Stack>
              <Stack
                direction="row"
                spacing={2}
                alignItems="center"
                justifyContent="space-between"
              >
                <Stack>
                  <Typography variant="h6">
                    {formats.formatNumber(
                      xTotalRecursoGarantidor() || 0,
                      2,
                      true,
                      true
                    )}
                  </Typography>
                </Stack>
                <Stack>
                  <IconButton
                    color="primary"
                    onClick={() =>
                      recursoContext.actions.handleDialogChanged("merge")
                    }
                  >
                    <Icon iconName="edit" size={18} />
                  </IconButton>
                </Stack>
              </Stack>
            </Stack>
          </Card>
        </Stack>
        <Stack flexGrow={1}>
          <Card>
            <Stack px={2} py={2}>
              <Typography variant="caption" color="textSecondary">
                Tipo
              </Typography>
              <Stack
                direction="row"
                spacing={2}
                alignItems="center"
                justifyContent="space-between"
              >
                <Stack>
                  <Typography variant="h6">
                    {CARTEIRA_TIPO[state.carteira_tipo_id].label}
                  </Typography>
                </Stack>
                <Stack>
                  <IconButton
                    color="primary"
                    onClick={actions.handleModifyTipo}
                  >
                    <Icon iconName="edit" size={18} />
                  </IconButton>
                </Stack>
              </Stack>
            </Stack>
          </Card>
        </Stack>
        {state.data_posicao && (
          <Stack flexGrow={1}>
            <Card>
              <Stack px={2} py={2}>
                <Typography variant="caption" color="textSecondary">
                  Data posição
                </Typography>
                <Stack
                  direction="row"
                  spacing={2}
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Stack>
                    <Typography variant="h6" color="textPrimary">
                      {formats.formatDateCustom(
                        state.data_posicao,
                        "DD/MM/YYYY"
                      )}
                    </Typography>
                  </Stack>
                  <Stack>
                    <CalendarPosicoesDialog />
                  </Stack>
                </Stack>
              </Stack>
            </Card>
          </Stack>
        )}
      </Stack>
    );
  }

  if (props.mode !== "read") {
    return (
      <form noValidate onSubmit={formik.handleSubmit} autoComplete="off">
        <Stack spacing={2}>
          <Stack direction="row" spacing={2}>
            <Stack flexGrow={1}>
              <TextField
                id="carteira"
                name="carteira"
                label="Carteira"
                value={formik.values.carteira}
                onChange={formik.handleChange}
                placeholder="Digite um nome"
                variant="outlined"
                InputLabelProps={{ shrink: true }}
                error={Boolean(formik.errors.carteira)}
                helperText={formik.errors.carteira}
                disabled={formik.isSubmitting}
              />
            </Stack>
            <Stack flexGrow={1}>
              <TextField
                id="carteira_seuid"
                name="carteira_seuid"
                label="SeuID"
                value={formik.values.carteira_seuid}
                onChange={formik.handleChange}
                placeholder="Digite um identificador para a carteira"
                variant="outlined"
                InputLabelProps={{ shrink: true }}
                error={Boolean(formik.errors.carteira_seuid)}
                helperText={formik.errors.carteira_seuid}
                disabled={formik.isSubmitting}
              />
            </Stack>
          </Stack>
          <Stack direction="row" spacing={2}>
            <FormControl>
              <FormLabel id="carteira_tipo_id-label">Tipo:</FormLabel>
              <RadioGroup
                row
                name="carteira_tipo_id"
                value={formik.values.carteira_tipo_id}
                onChange={formik.handleChange}
              >
                {Object.values(CARTEIRA_TIPO)
                  .filter((xTipo) => xTipo.id !== "C")
                  .map((xTipo) => {
                    return (
                      <FormControlLabel
                        key={xTipo.label}
                        value={xTipo.id}
                        control={<Radio />}
                        label={xTipo.label}
                      />
                    );
                  })}
              </RadioGroup>
            </FormControl>
          </Stack>
          <Stack
            flex={1}
            direction="row"
            justifyContent="flex-end"
            sx={{ py: 2 }}
            spacing={2}
          >
            <Button
              variant="outlined"
              type="button"
              color="primary"
              onClick={() => navigate(-1)}
            >
              Cancelar
            </Button>
            <LoadingButton
              startIcon={<Icon iconName="save" size={16} />}
              variant="contained"
              type="submit"
              loading={formik.isSubmitting}
              disabled={
                validators.isEmpty(formik.values.carteira) ||
                formik.isSubmitting ||
                !formik.isValid
              }
            >
              Salvar
            </LoadingButton>
          </Stack>
        </Stack>
      </form>
    );
  }

  return null;
});

Carteira.displayName = "Carteira";

export default Carteira;
